import type { StatusBadgeTone } from '@immo/ui-kit';
import type { DealProbability, DealStatus, PropertyListItem, WorkspaceMemberListItem } from '@immo/api-contract';
import type { MetaTone } from './components/phase-tag';

// The Status badge's tone, shared by every surface that shows one — the topbar
// breadcrumb, the deal header and (later) the rail. One map so two badges on the
// same page cannot disagree about what „Zurückgestellt" looks like.
export const DEAL_STATUS_TONE: Record<DealStatus, StatusBadgeTone> = {
  DRAFT: 'neutral',
  ACTIVE: 'success',
  WON: 'success',
  DEFERRED: 'neutral',
  LOST: 'neutral',
};

/** „Anna Schmidt, Ben Weber" — the owners in roster order; `fallback` when the deal has none. */
export function dealOwnersLabel(
  owners: ReadonlyArray<{ readonly id: string; readonly displayName: string | null }>,
  fallback: string,
): string {
  return owners.length > 0 ? owners.map((owner) => owner.displayName ?? owner.id.slice(0, 8)).join(', ') : fallback;
}

/** The 8-character upper-case short id. */
export function shortDealId(id: string): string {
  return id.replace(/-/g, '').slice(-8).toUpperCase();
}

export const currencyFormatter = new Intl.NumberFormat('de-DE', {
  style: 'currency',
  currency: 'EUR',
  maximumFractionDigits: 0,
});

/** `''` → null; a comma or dot decimal → the number; anything unparseable → NaN. */
export function parseAmount(raw: string): number | null {
  const trimmed = raw.trim();

  if (trimmed === '') return null;
  const normalised = Number(trimmed.replace(',', '.'));

  return Number.isFinite(normalised) ? normalised : NaN;
}

// „Tage in Phase" is no longer computed here (IMM-430.6): every surface reads
// the server's `activeDaysInPhase`, so the header, the list, the board's card,
// the board's lane order and the attention flag are one number by construction
// rather than by four call sites agreeing. The rule itself — freeze while the
// deal is on hold, and trust `phaseEnteredAt` to have completed holds already
// baked out by IMM-210 — now lives in `deal/src/domain/deal-attention.ts`.

export function actorInitials(actorId: string): string {
  return (
    actorId
      .replace(/[^a-z0-9]/gi, '')
      .slice(0, 2)
      .toUpperCase() || '—'
  );
}

type NamedMember = Pick<WorkspaceMemberListItem, 'firstName' | 'lastName'>;

export function memberName(member: NamedMember): string {
  return `${member.firstName} ${member.lastName}`.trim();
}

export function memberInitials(member: NamedMember): string {
  const first = member.firstName.trim()[0] ?? '';
  const last = member.lastName.trim()[0] ?? '';

  return `${first}${last}`.toUpperCase() || '—';
}

// HIGH reads as a win signal, LOW as a caution, MEDIUM sits in between —
// mirrors the reference deal-card meta-tag tones.
export function probabilityTone(probability: DealProbability): MetaTone {
  switch (probability) {
    case 'HIGH':
      return 'success';
    case 'MEDIUM':
      return 'warning';
    case 'LOW':
      return 'neutral';
  }
}

export function formatAddress(address: PropertyListItem['address']): string | null {
  const line = [address.street, address.houseNumber].filter((part) => part !== null && part.length > 0).join(' ');
  const parts = [line, address.city].filter((part) => part.length > 0);

  return parts.length > 0 ? parts.join(', ') : null;
}

/** „Straße 1, 12345 Ort" — the address with its postcode, for the Ort of an appointment; null when nothing is set. */
export function formatFullAddress(address: PropertyListItem['address']): string | null {
  const line = [address.street, address.houseNumber].filter((part) => part !== null && part.length > 0).join(' ');
  const place = [address.postalCode, address.city].filter((part) => part !== null && part.length > 0).join(' ');
  const parts = [line, place].filter((part) => part.length > 0);

  return parts.length > 0 ? parts.join(', ') : null;
}

/** How the board and the buyer-commission dialog name a Deal: its address, else the Objekt's title. */
export function dealPropertyLabel(property: PropertyListItem): string {
  return formatAddress(property.address) ?? property.title;
}
